import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { JobWithStats } from '@cvx/features/jobs/list_jobs_with_stats_db'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { cn } from '@rekrytering/ui/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalyticsSection } from '@/features/dashboard/components/analytics-section'
import { JobTagsEditor } from '@/features/jobs/components/job-tags-editor'
import { Route as JobDetailRoute } from '@/routes/_app/_auth/dashboard/_layout.jobs.$jobId'
import { Route as CreateJobRoute } from '@/routes/_app/_auth/dashboard/_layout.jobs.new'
import { useDoubleCheck } from '@/shared/hooks/use-double-check'

const STATUS_LABEL_KEY = {
  draft: 'jobs.statusDraft',
  active: 'jobs.statusActive',
  paused: 'jobs.statusPaused',
} as const

const STATUS_CLASS = {
  draft:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  active:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  paused:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
} as const

type SaveAsTemplateDialogState =
  | { readonly status: 'closed' }
  | { readonly status: 'open'; readonly defaultName: string }

function JobCard({ job }: { job: JobWithStats }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [saveAsTemplateState, setSaveAsTemplateState] =
    useState<SaveAsTemplateDialogState>({ status: 'closed' })
  const [templateNameInput, setTemplateNameInput] = useState('')
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const {
    doubleCheck: deleteDoubleCheck,
    getButtonProps: getDeleteButtonProps,
  } = useDoubleCheck()
  const { doubleCheck: pauseDoubleCheck, getButtonProps: getPauseButtonProps } =
    useDoubleCheck()

  const { mutateAsync: deleteJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.deleteMyJob),
  })
  const { mutateAsync: duplicateJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.duplicateMyJob),
  })
  const { mutateAsync: pauseJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.pauseMyJob),
  })
  const { mutateAsync: saveAsTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.job_templates.saveMyJobAsTemplate,
    ),
  })

  const handleOpenSaveAsTemplate = (): void => {
    setTemplateNameInput(job.title)
    setSaveAsTemplateState({ status: 'open', defaultName: job.title })
  }

  const handleSaveAsTemplate = async (): Promise<void> => {
    if (!templateNameInput.trim()) return
    setIsSavingTemplate(true)
    await saveAsTemplate({ jobId: job._id, name: templateNameInput.trim() })
    setIsSavingTemplate(false)
    setSaveAsTemplateState({ status: 'closed' })
  }

  return (
    <>
      {saveAsTemplateState.status === 'open' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-border bg-card p-6 shadow-lg">
            <h3 className="text-base font-semibold text-primary">
              {t('templates.saveAsTemplateTitle')}
            </h3>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                {t('templates.templateNameLabel')}
              </label>
              <Input
                autoFocus
                value={templateNameInput}
                onChange={(e) => setTemplateNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveAsTemplate()
                  if (e.key === 'Escape')
                    setSaveAsTemplateState({ status: 'closed' })
                }}
                placeholder={t('templates.templateNamePlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSaveAsTemplateState({ status: 'closed' })}
              >
                {t('jobs.form.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAsTemplate}
                disabled={isSavingTemplate || !templateNameInput.trim()}
              >
                {isSavingTemplate ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  t('templates.saveButton')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div
        className="flex cursor-pointer flex-col gap-4 rounded-lg border border-border bg-card p-5 transition hover:bg-primary/5"
        onClick={() =>
          navigate({ to: JobDetailRoute.fullPath, params: { jobId: job._id } })
        }
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <p className="text-sm font-medium text-primary truncate">
              {job.title}
            </p>
            <p className="line-clamp-2 text-xs text-primary/60">
              {job.description}
            </p>
          </div>
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_CLASS[job.status],
            )}
          >
            {t(STATUS_LABEL_KEY[job.status])}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-md bg-secondary px-3 py-2">
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-base font-semibold text-primary">
              {job.stats.candidatesInvited}
            </span>
            <span className="text-xs text-primary/50">
              {t('dashboard.statsInvited')}
            </span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-base font-semibold text-primary">
              {job.stats.candidatesCompleted}
            </span>
            <span className="text-xs text-primary/50">
              {t('dashboard.statsCompleted')}
            </span>
          </div>
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-base font-semibold text-primary">
              {job.stats.candidatesAdvanced}
            </span>
            <span className="text-xs text-primary/50">
              {t('dashboard.statsAdvanced')}
            </span>
          </div>
        </div>

        {job.stats.averageScore !== null && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-primary/50">
              {t('dashboard.statsAvgScore')}
            </span>
            <span className="text-xs font-medium text-primary">
              {Math.round(job.stats.averageScore)}
            </span>
          </div>
        )}

        <div onClick={(e) => e.stopPropagation()}>
          <JobTagsEditor jobId={job._id} />
        </div>

        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            size="sm"
            variant="ghost"
            onClick={() => duplicateJob({ jobId: job._id })}
          >
            {t('jobs.duplicate')}
          </Button>
          {job.status === 'active' && (
            <Button
              size="sm"
              variant="ghost"
              {...getPauseButtonProps({
                onClick: pauseDoubleCheck
                  ? () => pauseJob({ jobId: job._id })
                  : undefined,
              })}
            >
              {pauseDoubleCheck ? t('jobs.pauseConfirm') : t('jobs.pause')}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            {...getDeleteButtonProps({
              onClick: deleteDoubleCheck
                ? () => deleteJob({ jobId: job._id })
                : undefined,
            })}
          >
            {deleteDoubleCheck ? t('jobs.deleteConfirm') : t('jobs.delete')}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleOpenSaveAsTemplate}>
            {t('templates.saveAsTemplate')}
          </Button>
        </div>
      </div>
    </>
  )
}

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: jobs = [] } = useQuery(
    convexQuery(api.features.jobs.jobs.listMyJobsWithStats, {}),
  )

  return (
    <div className="flex h-full w-full px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
        <div className="flex items-center justify-between">
          <div />
          <Button
            size="sm"
            onClick={() => navigate({ to: CreateJobRoute.fullPath })}
          >
            <Plus className="h-4 w-4" />
            {t('jobs.createJob')}
          </Button>
        </div>

        <AnalyticsSection />

        {jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
            <p className="text-lg font-medium text-primary/60">
              {t('jobs.noJobs')}
            </p>
            <p className="text-sm text-primary/40">
              {t('jobs.noJobsDescription')}
            </p>
            <Button
              size="sm"
              onClick={() => navigate({ to: CreateJobRoute.fullPath })}
            >
              <Plus className="h-4 w-4" />
              {t('jobs.createJob')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job) => (
              <JobCard key={job._id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
